/**
 * SeoAuditModal — displays an AI-generated whole-site SEO audit report:
 *   • overall score
 *   • strengths / issues (with severity)
 *   • global suggestions (apply to site-wide defaults)
 *   • per-page recommendations (apply to that page's SEO)
 *   • quick wins
 *
 * Props:
 *   show     — boolean
 *   onHide   — close handler
 *   loading  — true while the audit is running
 *   report   — normalized report from POST /api/cms/ai/audit-seo
 *   tokensUsed — number (shown in footer)
 *   onApplyGlobal({ defaultMetaTitle, defaultMetaDescription, defaultKeywords, titleSuffix })
 *   onApplyPage(pageType, { metaTitle, metaDescription, keywords })
 *   onRerun   — re-run audit handler
 */

import React from 'react';
import {
  AlertTriangle, CheckCircle2, Sparkles, RefreshCw, Zap, Target, Globe2,
} from 'lucide-react';
import { AppModal, AppButton } from '../ui';

// ── Class strings ─────────────────────────────────────────────────────────
// Converted to Tailwind 2026-08-25 (Phase 5); seo-audit-modal.scss deleted with
// it. The sheet held **zero colour literals** — every colour was already a
// token — so this was a pure mechanism change, and the value is in what it
// retires: fourteen references to Phase 1's `_aliases.scss` scaffolding
// (`--color-success`, `--color-warning`, `--color-danger`, `--brand-primary`,
// `--brand-bg`), which 5.2 deletes once the last reader is gone.
//
// `seoAuditPulse` moved to tailwind.config.js as `pulse-scale`.
const SEV_ICON = "tw-mt-0.5 tw-shrink-0";

const SCORE =
  "tw-flex tw-h-24 tw-w-24 tw-shrink-0 tw-flex-col tw-items-center tw-justify-center " +
  "tw-rounded-full tw-border-4 tw-bg-surface";

// h4/p inside the hero and the loading state are prose the component does not
// wrap, so they are arbitrary variants rather than a class per element.
const HERO_TEXT =
  "tw-flex-1 [&_h4]:tw-mb-1.5 [&_h4]:tw-mt-0 [&_h4]:tw-text-base [&_h4]:tw-text-content " +
  "[&_p]:tw-m-0 [&_p]:tw-text-sm [&_p]:tw-leading-[1.5] [&_p]:tw-text-content-muted";

const SECTION =
  "tw-flex tw-flex-col tw-gap-2.5 " +
  "[&>h5]:tw-m-0 [&>h5]:tw-inline-flex [&>h5]:tw-items-center [&>h5]:tw-gap-1.5 " +
  "[&>h5]:tw-text-[0.8125rem] [&>h5]:tw-font-semibold [&>h5]:tw-uppercase " +
  "[&>h5]:tw-tracking-[0.04em] [&>h5]:tw-text-content-muted";

const LIST =
  "tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2 tw-p-0 " +
  "[&>li]:tw-flex [&>li]:tw-items-start [&>li]:tw-gap-2 [&>li]:tw-rounded-[6px] " +
  "[&>li]:tw-border [&>li]:tw-border-line [&>li]:tw-bg-surface-alt [&>li]:tw-px-3 " +
  "[&>li]:tw-py-2 [&>li]:tw-text-sm [&>li]:tw-leading-[1.45] [&>li]:tw-text-content";
const LIST_OK = "[&>li>svg]:tw-mt-0.5 [&>li>svg]:tw-shrink-0 [&>li>svg]:tw-text-success";

const ISSUE = "tw-items-start [&>div]:tw-flex [&>div]:tw-flex-col [&>div]:tw-gap-[0.125rem]";
const ISSUE_EDGE = {
  high: "tw-border-l-[3px] tw-border-l-danger",
  medium: "tw-border-l-[3px] tw-border-l-warning",
  low: "tw-border-l-[3px] tw-border-l-[var(--text-tertiary)]",
};

const SUGGESTION =
  "tw-flex tw-flex-col tw-gap-2 tw-rounded-sm tw-border tw-border-primary tw-bg-surface-sunken " +
  "tw-p-3.5 tw-text-sm tw-text-content " +
  "[&>div]:tw-flex [&>div]:tw-flex-wrap [&>div]:tw-items-baseline [&>div]:tw-gap-2 " +
  "[&_strong]:tw-min-w-[130px] [&_strong]:tw-font-semibold [&_strong]:tw-text-content-muted";

const CHIP =
  "tw-rounded-full tw-border tw-border-line tw-bg-surface-sunken tw-px-2.5 tw-py-[0.125rem] " +
  "tw-text-xs tw-text-content-muted";

const PAGE_CARD =
  "tw-flex tw-flex-col tw-gap-1.5 tw-rounded-sm tw-border tw-border-line tw-bg-surface-alt " +
  "tw-p-3.5 tw-text-[0.8125rem] tw-text-content " +
  "[&_strong]:tw-me-1 [&_strong]:tw-font-semibold [&_strong]:tw-text-content-muted";

const PAGE_TYPE =
  "tw-rounded-full tw-bg-surface-sunken tw-px-2.5 tw-py-[0.125rem] tw-text-[0.6875rem] " +
  "tw-font-semibold tw-uppercase tw-tracking-[0.04em] tw-text-primary";

const LOADING =
  "tw-flex tw-flex-col tw-items-center tw-px-4 tw-py-8 tw-text-center tw-text-content-muted " +
  "[&_h4]:tw-mb-1 [&_h4]:tw-mt-2 [&_h4]:tw-text-content [&_p]:tw-m-0 [&_p]:tw-max-w-[460px] [&_p]:tw-text-sm";

const FOOTER =
  "tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 " +
  "[&_small]:tw-text-xs [&_small]:tw-text-content-subtle";

const severityIcon = (sev) => {
  if (sev === 'high') return <AlertTriangle size={14} className={`${SEV_ICON} tw-text-danger`} />;
  if (sev === 'medium') return <AlertTriangle size={14} className={`${SEV_ICON} tw-text-warning`} />;
  return <AlertTriangle size={14} className={`${SEV_ICON} tw-text-content-subtle`} />;
};

const scoreClass = (score) => {
  if (score >= 80) return 'tw-border-success tw-text-success';
  if (score >= 50) return 'tw-border-warning tw-text-warning';
  return 'tw-border-danger tw-text-danger';
};

const SeoAuditModal = ({
  show, onHide, loading, report, tokensUsed,
  onApplyGlobal, onApplyPage, onRerun,
}) => {
  const renderBody = () => {
    if (loading) {
      return (
        <div className={LOADING}>
          <Sparkles size={28} className="tw-animate-pulse-scale tw-text-primary" />
          <h4>Analysing your website…</h4>
          <p>The AI is reviewing your pages, courses, blogs and current SEO settings. This usually takes 10-20 seconds.</p>
        </div>
      );
    }
    if (!report) {
      return (
        <div className="tw-px-4 tw-py-8 tw-text-center tw-text-content-muted">
          <p>No audit report available yet. Click <strong>Run audit</strong> to start.</p>
        </div>
      );
    }

    const score = report.overallScore || 0;
    const gs = report.globalSuggestions || {};
    const hasGlobalSuggestion = !!(gs.defaultMetaTitle || gs.defaultMetaDescription || (gs.defaultKeywords || []).length || gs.titleSuffix);

    return (
      <div className="tw-flex tw-flex-col tw-gap-5">
        {/* SCORE + SUMMARY */}
        <div className="tw-flex tw-items-center tw-gap-4 tw-rounded-md tw-border tw-border-line tw-bg-surface-alt tw-p-4">
          <div className={`${SCORE} ${scoreClass(score)}`}>
            <div className="tw-text-[1.75rem] tw-font-medium tw-leading-none">{score}</div>
            <div className="tw-mt-0.5 tw-text-[0.6875rem] tw-text-content-subtle">/ 100</div>
          </div>
          <div className={HERO_TEXT}>
            <h4>Overall SEO health</h4>
            <p>{report.summary}</p>
          </div>
        </div>

        {/* QUICK WINS */}
        {(report.quickWins || []).length > 0 && (
          <section className={SECTION}>
            <h5><Zap size={14} /> Quick wins</h5>
            <ul className={`${LIST} ${LIST_OK}`}>
              {report.quickWins.map((q, i) => (
                <li key={i}><CheckCircle2 size={14} /> {q}</li>
              ))}
            </ul>
          </section>
        )}

        {/* ISSUES */}
        {(report.issues || []).length > 0 && (
          <section className={SECTION}>
            <h5><AlertTriangle size={14} /> Issues to fix ({report.issues.length})</h5>
            <ul className={LIST}>
              {report.issues.map((it, i) => (
                <li key={i} className={`${ISSUE} ${ISSUE_EDGE[it.severity] || ISSUE_EDGE.low}`}>
                  {severityIcon(it.severity)}
                  <div>
                    <span className="tw-text-[0.6875rem] tw-uppercase tw-tracking-[0.04em] tw-text-content-subtle">{it.area}</span>
                    <span className="tw-text-content">{it.message}</span>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* STRENGTHS */}
        {(report.strengths || []).length > 0 && (
          <section className={SECTION}>
            <h5><CheckCircle2 size={14} /> Strengths</h5>
            <ul className={`${LIST} ${LIST_OK}`}>
              {report.strengths.map((s, i) => (
                <li key={i}><CheckCircle2 size={14} /> {s}</li>
              ))}
            </ul>
          </section>
        )}

        {/* GLOBAL SUGGESTIONS */}
        {hasGlobalSuggestion && (
          <section className={SECTION}>
            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
              <h5><Globe2 size={14} /> Site-wide improvements</h5>
              <AppButton variant="primary" size="sm" onClick={() => onApplyGlobal?.(gs)}>
                Apply to defaults
              </AppButton>
            </div>
            <div className={SUGGESTION}>
              {gs.defaultMetaTitle && (
                <div><strong>Default title:</strong> <span>{gs.defaultMetaTitle}</span></div>
              )}
              {gs.titleSuffix && (
                <div><strong>Title suffix:</strong> <span>{gs.titleSuffix}</span></div>
              )}
              {gs.defaultMetaDescription && (
                <div><strong>Default description:</strong> <span>{gs.defaultMetaDescription}</span></div>
              )}
              {(gs.defaultKeywords || []).length > 0 && (
                <div>
                  <strong>Default keywords:</strong>
                  <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-1.5">
                    {gs.defaultKeywords.map((k, i) => (
                      <span key={i} className={CHIP}>{k}</span>
                    ))}
                  </div>
                </div>
              )}
              {gs.rationale && <div className="tw-mt-1 tw-text-[0.8125rem] tw-text-content-subtle"><em>{gs.rationale}</em></div>}
            </div>
          </section>
        )}

        {/* PER-PAGE RECOMMENDATIONS */}
        {(report.pageRecommendations || []).length > 0 && (
          <section className={SECTION}>
            <h5><Target size={14} /> Per-page recommendations</h5>
            <div className="tw-grid tw-grid-cols-[repeat(auto-fill,minmax(320px,1fr))] tw-gap-3">
              {report.pageRecommendations.map((p, i) => (
                <div key={i} className={PAGE_CARD}>
                  <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
                    <span className={PAGE_TYPE}>{p.pageType}</span>
                    {onApplyPage && (
                      <AppButton variant="secondary" size="sm" onClick={() => onApplyPage(p.pageType, p)}>
                        Apply to {p.pageType}
                      </AppButton>
                    )}
                  </div>
                  {p.metaTitle && <div><strong>Title:</strong> {p.metaTitle}</div>}
                  {p.metaDescription && <div><strong>Description:</strong> {p.metaDescription}</div>}
                  {(p.keywords || []).length > 0 && (
                    <div className="tw-mt-1 tw-flex tw-flex-wrap tw-gap-1.5">
                      {p.keywords.map((k, j) => (
                        <span key={j} className={CHIP}>{k}</span>
                      ))}
                    </div>
                  )}
                  {p.rationale && <div className="tw-mt-1 tw-text-[0.8125rem] tw-text-content-subtle"><em>{p.rationale}</em></div>}
                </div>
              ))}
            </div>
          </section>
        )}
      </div>
    );
  };

  return (
    <AppModal
      show={show}
      onHide={onHide}
      size="xl"
      icon={<Sparkles size={18} />}
      title="AI SEO Audit"
      footer={
        <div className={FOOTER}>
          <small>
            {tokensUsed != null
              ? `Used ${tokensUsed.toLocaleString()} AI tokens from your monthly CMS quota.`
              : 'Tokens are deducted from your monthly CMS AI quota.'}
          </small>
          <div className="tw-flex tw-gap-2">
            <AppButton variant="ghost" onClick={onHide}>Close</AppButton>
            {onRerun && !loading && (
              <AppButton variant="secondary" onClick={onRerun}>
                <RefreshCw size={14} /> Re-run audit
              </AppButton>
            )}
          </div>
        </div>
      }
    >
      {renderBody()}
    </AppModal>
  );
};

export default SeoAuditModal;
